import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getEvidenceData, getReportMetadata, ReportMetadataRecord } from "../../../api/reportApi";
import { getAccessToken } from "../../../api/clients/tokenAccessor";
import { AGENT_API_BASE_URL } from "../../../shared/constants";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { useJobStore } from "../../../store/jobStore";
import {
  filterStalePlanInterrupts,
  normalizePlanPresentation,
  resolvePlanInterrupts,
  resolvePlanThreadId,
  selectPlanInterrupt,
} from "../utils";
import { notifyPlanReady } from "../../../utils/notifyPlanReady";
import type { PlanReviewResponse, PlanReviewThread, ReviewEntity, ReviewGap } from "../types";

const REVISION_POLL_INTERVAL_MS = 2_000;
const REVISION_TIMEOUT_MS = 120_000;

export const formatEntityEditsForFeedback = (entities: ReviewEntity[]): string => {
  if (entities.length === 0) return "";
  const byCategory = new Map<string, ReviewEntity[]>();
  for (const entity of entities) {
    const list = byCategory.get(entity.category);
    if (list) list.push(entity);
    else byCategory.set(entity.category, [entity]);
  }
  const lines: string[] = [
    "Officer-revised entity outline (treat this as the desired final entity state):",
  ];
  for (const [category, list] of byCategory) {
    lines.push(`- ${category}:`);
    for (const entity of list) {
      const label = entity.label.trim() || "(untitled)";
      const detail = entity.details?.trim() ? ` — ${entity.details.trim()}` : "";
      const flag = entity.removed ? " [REMOVED]" : "";
      lines.push(`  - ${label}${detail}${flag}`);
    }
  }
  return lines.join("\n");
};

export const buildRevisionFeedback = (
  feedback: string,
  entities: ReviewEntity[],
  entitiesEdited: boolean,
  gaps: ReviewGap[]
): string => {
  const trimmedFeedback = feedback.trim();
  const clarifiedGaps = gaps.filter((gap) => gap.resolution?.trim());

  const sections: string[] = [];
  if (trimmedFeedback) {
    sections.push(trimmedFeedback);
  }

  // Always include the officer's revised entity outline when they edited
  // entities — the planner has no other channel to receive entity changes
  // (the present_plan tool only reads `approved` and `feedback` on
  // resume; structured `entityEdits` payloads were silently dropped).
  if (entitiesEdited) {
    const entityBlock = formatEntityEditsForFeedback(entities);
    if (entityBlock) sections.push(entityBlock);
  }

  if (clarifiedGaps.length > 0) {
    const clarificationLines = clarifiedGaps.map(
      (gap) => `- ${gap.description}: ${gap.resolution!.trim()}`
    );
    sections.push(`Officer clarifications:\n${clarificationLines.join("\n")}`);
  }

  if (sections.length === 0) return "";

  // Provide a leading instruction when the only signal is structured
  // (clarifications or entity edits), so the planner doesn't see a bare
  // outline with no directive.
  if (!trimmedFeedback) {
    sections.unshift("Please revise the outline using the officer's input below.");
  }

  return sections.join("\n\n");
};

const buildAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getAccessToken();
  return { Authorization: `Bearer ${token}` };
};

export const usePlanReview = () => {
  const { reportID: routeThreadId } = useParams<{ reportID?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const updateJobStatus = useJobStore((state) => state.updateJobStatus);
  const startPollingJob = useJobStore((state) => state.startPollingJob);

  const threadId = useMemo(
    () => resolvePlanThreadId(routeThreadId, searchParams, location.state),
    [location.state, routeThreadId, searchParams]
  );

  const [thread, setThread] = useState<PlanReviewThread | null>(null);
  const [reportMetadata, setReportMetadata] = useState<ReportMetadataRecord | null>(null);
  const [videoData, setVideoData] = useState<VideoData | VideoData[] | null>(null);
  const [feedback, setFeedback] = useState("");
  const [submittedInterruptId, setSubmittedInterruptId] = useState<string | null>(null);
  const [revisionStartedAt, setRevisionStartedAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [evidenceError, setEvidenceError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const visibleInterrupts = useMemo(
    () => filterStalePlanInterrupts(thread, submittedInterruptId),
    [submittedInterruptId, thread]
  );
  const currentInterrupt = useMemo(
    () => selectPlanInterrupt(visibleInterrupts),
    [visibleInterrupts]
  );
  const currentPlan = useMemo(
    () => normalizePlanPresentation(currentInterrupt),
    [currentInterrupt]
  );
  const isAwaitingRevision = Boolean(submittedInterruptId);

  // ── Entity & gap state ─────────────────────────────
  const [entities, setEntities] = useState<ReviewEntity[]>([]);
  const [gaps, setGaps] = useState<ReviewGap[]>([]);
  // Tracks whether the officer has touched the entity outline since the
  // current plan was loaded. Used by buildRevisionFeedback to decide
  // whether to include the revised entity block in the feedback text on
  // Request Changes — without this flag, every revision would echo the
  // entity list back to the planner whether the officer edited it or not.
  const [entitiesEdited, setEntitiesEdited] = useState(false);

  // Sync from plan when a new plan arrives. Reset the edited flag —
  // any prior edits have already been transmitted (or discarded).
  useEffect(() => {
    if (currentPlan?.entities) setEntities(currentPlan.entities);
    else setEntities([]);
    if (currentPlan?.gaps) setGaps(currentPlan.gaps);
    else setGaps([]);
    setEntitiesEdited(false);
  }, [currentPlan]);

  const updateEntity = useCallback((id: string, patch: Partial<ReviewEntity>) => {
    setEntities((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    setEntitiesEdited(true);
  }, []);

  const removeEntity = useCallback((id: string) => {
    setEntities((prev) => prev.map((e) => (e.id === id ? { ...e, removed: true } : e)));
    setEntitiesEdited(true);
  }, []);

  const restoreEntity = useCallback((id: string) => {
    setEntities((prev) => prev.map((e) => (e.id === id ? { ...e, removed: false } : e)));
    setEntitiesEdited(true);
  }, []);

  const addEntity = useCallback((category: string) => {
    setEntities((prev) => [
      ...prev,
      { id: crypto.randomUUID(), category, label: "", details: "" },
    ]);
    setEntitiesEdited(true);
  }, []);

  const reorderEntities = useCallback((category: string, fromIndex: number, toIndex: number) => {
    setEntities((prev) => {
      const catItems = prev.filter((e) => e.category === category);
      const [moved] = catItems.splice(fromIndex, 1);
      catItems.splice(toIndex, 0, moved);
      // Rebuild in original category order
      const result: ReviewEntity[] = [];
      const catQueue = [...catItems];
      let catIdx = 0;
      for (const item of prev) {
        if (item.category === category) {
          result.push(catQueue[catIdx++]);
        } else {
          result.push(item);
        }
      }
      return result;
    });
    setEntitiesEdited(true);
  }, []);

  const resolveGap = useCallback((id: string, resolution: string) => {
    setGaps((prev) => prev.map((g) => (g.id === id ? { ...g, resolution } : g)));
  }, []);

  const markJobResumed = useCallback(() => {
    if (!threadId) return;

    const existingJob = useJobStore
      .getState()
      .jobs.find((job) => job.id === threadId || job.reportId === threadId);

    if (existingJob) {
      updateJobStatus(existingJob.id, ReportJobStatus.PENDING);
      startPollingJob(existingJob.id);
    }
  }, [startPollingJob, threadId, updateJobStatus]);

  const refreshThread = useCallback(
    async ({
      silent = false,
      respondedInterruptId,
    }: {
      silent?: boolean;
      respondedInterruptId?: string | null;
    } = {}): Promise<PlanReviewThread | null> => {
      if (!threadId || !AGENT_API_BASE_URL) return null;

      if (!silent) {
        setIsLoading(true);
      }
      setError("");

      try {
        const response = await fetch(`${AGENT_API_BASE_URL}/get-thread`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(await buildAuthHeaders()),
          },
          body: JSON.stringify({ threadId }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Failed to load outline review.");
        }

        const data = (await response.json()) as PlanReviewThread;
        setThread(data);

        if (data.status === "FAILED") {
          setStatusMessage("");
          setError(typeof data.error === "string" ? data.error : "AI Assistant failed while waiting for input.");
          return data;
        }

        const nextVisibleInterrupts = filterStalePlanInterrupts(
          data,
          respondedInterruptId ?? submittedInterruptId
        );

        if (data.status === "RUNNING") {
          markJobResumed();
          setStatusMessage(
            submittedInterruptId
              ? "AI Assistant is revising the outline."
              : "AI Assistant resumed successfully."
          );
          return data;
        }

        if (data.status === "AWAIT" && nextVisibleInterrupts.length > 0) {
          if (respondedInterruptId ?? submittedInterruptId) {
            setSubmittedInterruptId(null);
            setRevisionStartedAt(null);
            setFeedback("");
            setStatusMessage("A revised outline is ready for review.");
            notifyPlanReady();
          } else {
            setStatusMessage("Review the proposed outline before report generation continues.");
          }
          return data;
        }

        if (data.status === "AWAIT" && nextVisibleInterrupts.length === 0) {
          const rawInterrupts = resolvePlanInterrupts(data);
          const activeRespondedInterruptId = respondedInterruptId ?? submittedInterruptId;

          if (
            activeRespondedInterruptId &&
            rawInterrupts.some(
              (interrupt) => interrupt.interruptId === activeRespondedInterruptId
            )
          ) {
            setStatusMessage("Waiting for the AI Assistant to pick up your feedback.");
            return data;
          }

          if (activeRespondedInterruptId) {
            setStatusMessage("AI Assistant continued without another approval step.");
            navigate("/");
            return data;
          }
        }

        setStatusMessage("");
        return data;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load outline review.");
        return null;
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [AGENT_API_BASE_URL, markJobResumed, navigate, submittedInterruptId, threadId]
  );

  const refreshMetadata = useCallback(async () => {
    if (!threadId) {
      setReportMetadata(null);
      setVideoData(null);
      setEvidenceError("");
      return;
    }

    setIsLoadingEvidence(true);
    setEvidenceError("");

    // Try report metadata first (available after report is generated)
    try {
      const nextMetadata = await getReportMetadata(threadId);
      setReportMetadata(nextMetadata);
    } catch {
      // Report doesn't exist yet during plan review — that's expected
      setReportMetadata(null);
    }

    // Resolve evidence IDs: prefer job store (always available), fall back to metadata
    const job = useJobStore
      .getState()
      .jobs.find((j) => j.id === threadId || j.reportId === threadId);
    const evidenceIds = job?.evidenceIDs ?? [];

    if (!evidenceIds.length) {
      setVideoData(null);
      setIsLoadingEvidence(false);
      return;
    }

    try {
      const evidenceResults = await Promise.allSettled(
        evidenceIds.map((id) => getEvidenceData(id))
      );
      const successfulEvidence = evidenceResults.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : []
      );
      const failedEvidenceCount = evidenceResults.length - successfulEvidence.length;

      setVideoData(
        successfulEvidence.length === 0
          ? null
          : successfulEvidence.length === 1
            ? successfulEvidence[0]
            : successfulEvidence
      );

      if (failedEvidenceCount > 0) {
        setEvidenceError(
          successfulEvidence.length > 0
            ? "Some evidence previews could not be loaded."
            : "Evidence preview could not be loaded."
        );
      }
    } catch (err) {
      setVideoData(null);
      setEvidenceError(
        err instanceof Error ? err.message : "Failed to load evidence."
      );
    } finally {
      setIsLoadingEvidence(false);
    }
  }, [threadId]);

  useEffect(() => {
    void refreshThread();
    void refreshMetadata();
  }, [refreshMetadata, refreshThread]);

  useEffect(() => {
    if (!submittedInterruptId || !revisionStartedAt) return;

    const intervalId = window.setInterval(() => {
      if (Date.now() - revisionStartedAt >= REVISION_TIMEOUT_MS) {
        setSubmittedInterruptId(null);
        setRevisionStartedAt(null);
        setStatusMessage("Outline revision is taking longer than expected. Refresh to try again.");
        return;
      }

      void refreshThread({ silent: true });
    }, REVISION_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [refreshThread, revisionStartedAt, submittedInterruptId]);

  const submitHitlResponse = useCallback(
    async (userInput: unknown) => {
      if (!threadId || !AGENT_API_BASE_URL || !currentInterrupt) return null;

      const response = await fetch(`${AGENT_API_BASE_URL}/receive`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await buildAuthHeaders()),
        },
        body: JSON.stringify({
          threadId,
          hitlResponses: [
            {
              interruptId: currentInterrupt.interruptId,
              userInput,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 409) {
          throw new Error("Report is no longer waiting for input.");
        }
        throw new Error(errorText || "Failed to submit outline review.");
      }

      return (await response.json()) as PlanReviewResponse;
    },
    [AGENT_API_BASE_URL, currentInterrupt, threadId]
  );

  const submitApproval = useCallback(async () => {
    if (!currentInterrupt) return;

    setIsSubmitting(true);
    setError("");

    try {
      // Officer overrides on approval: typed comments, edited entity
      // outline, and resolved gap clarifications all ride into the
      // present_plan resume payload so the orchestrator (via
      // present-plan-body.ts officer_* XML sections) honors them when
      // delegating to extractors. The legacy `entityEdits` structured
      // field was dropped — backend never read it.
      const officerComments = feedback.trim() || undefined;
      const officerEntityOverrides = entitiesEdited
        ? formatEntityEditsForFeedback(entities)
        : undefined;
      const officerClarifications = gaps
        .filter((g) => g.resolution?.trim())
        .map((g) => ({
          description: g.description,
          resolution: g.resolution!.trim(),
        }));

      await submitHitlResponse({
        approved: true,
        ...(officerComments ? { officerComments } : {}),
        ...(officerEntityOverrides ? { officerEntityOverrides } : {}),
        ...(officerClarifications.length > 0 ? { officerClarifications } : {}),
      });
      markJobResumed();
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve outline.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentInterrupt,
    entities,
    entitiesEdited,
    feedback,
    gaps,
    markJobResumed,
    navigate,
    submitHitlResponse,
  ]);

  const submitFeedback = useCallback(async () => {
    if (!currentInterrupt) return;
    const revisionFeedback = buildRevisionFeedback(
      feedback,
      entities,
      entitiesEdited,
      gaps
    );

    if (!revisionFeedback.trim()) {
      setError("Enter feedback before requesting changes.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await submitHitlResponse({
        approved: false,
        feedback: revisionFeedback,
      });
      markJobResumed();
      setSubmittedInterruptId(currentInterrupt.interruptId);
      setRevisionStartedAt(Date.now());
      setStatusMessage("AI Assistant is revising the outline.");
      await refreshThread({
        silent: true,
        respondedInterruptId: currentInterrupt.interruptId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit outline changes.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentInterrupt,
    entities,
    entitiesEdited,
    feedback,
    gaps,
    markJobResumed,
    refreshThread,
    submitHitlResponse,
  ]);

  const handleReturnHome = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const job = useJobStore
    .getState()
    .jobs.find((j) => j.id === threadId || j.reportId === threadId);

  return {
    threadId,
    thread,
    reportMetadata,
    videoData,
    videoTitles: job?.videoTitles,
    currentInterrupt,
    currentPlan,
    visibleInterrupts,
    feedback,
    setFeedback,
    isLoading,
    isLoadingEvidence,
    isSubmitting,
    isAwaitingRevision,
    error,
    evidenceError,
    statusMessage,
    submitApproval,
    submitFeedback,
    handleReturnHome,
    refreshThread,
    entities,
    entitiesEdited,
    gaps,
    updateEntity,
    removeEntity,
    restoreEntity,
    addEntity,
    reorderEntities,
    resolveGap,
    revisionStartedAt,
    revisionTimeoutMs: REVISION_TIMEOUT_MS,
  };
};
